/**
 * Excel de la VISTA PREVIA de la importación de Libros Digitales: lo que va a pasar con cada
 * código antes de importar, para revisarlo con calma.
 *
 * Sin React ni antd a propósito, igual que la plantilla y el análisis: el libro se puede armar
 * y revisar con un `node script.js`.
 *
 * Recibe los `datos` de cada fila tal como los arma `analizarArchivo` (analizarLibrosDigitales.js).
 */

import * as XLSXStyle from "xlsx-js-style";
import {
  celdaNumero,
  celdaTexto,
  headerStyle,
  numeroStyle,
  textStyle,
  totalTextoStyle,
} from "../../../../../Components/ExcelEstilos";
import { ESTADOS, etiquetaEstado } from "./analizarLibrosDigitales";

export const NOMBRE_ARCHIVO_VISTA_PREVIA = "vistaPreviaVentasLibrosDigitales.xlsx";

// Las que la importación NO va a procesar: el usuario las tiene que revisar a mano.
export const ESTADOS_A_REVISAR = ["otroCliente", "timbrada", "enGlobal", "error"];

// Relleno suave de la celda "Qué va a pasar", por grupo, para leer la hoja de un vistazo.
const RELLENO_POR_ESTADO = {
  creada: "E8F5E1", // verde: se crea la venta
  reasignada: "E6F7FF", // azul: se le pasa la venta al alumno
  separada: "E6F7FF",
  agrupada: "F2F2F2", // gris: va junto con otra fila / solo correo
  yaAsignada: "F2F2F2",
  otroCliente: "FFF1E0", // naranja: no se toca
  timbrada: "FFF1E0",
  enGlobal: "FFF1E0",
  error: "FDE7E9", // rojo: revisar
};

const estiloEstado = (estado) => {
  const rgb = RELLENO_POR_ESTADO[estado];
  return rgb ? { ...textStyle, font: { bold: true }, fill: { fgColor: { rgb } } } : textStyle;
};

const siNo = (valor) => (valor ? "Sí" : "No");

const COLUMNAS = [
  { titulo: "Fila", ancho: 7, valor: (f) => f.numeroFila, numero: true },
  { titulo: "Matrícula", ancho: 18, valor: (f) => f.matricula },
  { titulo: "Alumno", ancho: 32, valor: (f) => f.alumno },
  { titulo: "Código del libro", ancho: 26, valor: (f) => f.numeroLote },
  { titulo: "Libro", ancho: 30, valor: (f) => f.insumo },
  {
    titulo: "Qué va a pasar",
    ancho: 24,
    valor: (f) => etiquetaEstado(f.estadoPrevisto),
    estilo: (f) => estiloEstado(f.estadoPrevisto),
  },
  { titulo: "Venta encontrada", ancho: 16, valor: (f) => f.folio },
  { titulo: "Cliente de la venta", ancho: 32, valor: (f) => f.clienteVenta },
  { titulo: "RFC", ancho: 16, valor: (f) => f.rfcVenta },
  { titulo: "UUID", ancho: 38, valor: (f) => f.uuid },
  { titulo: "Se procesa con la fila", ancho: 12, valor: (f) => f.agrupadaCon ?? "", numero: true },
  { titulo: "Códigos en la operación", ancho: 12, valor: (f) => f.librosAgrupados ?? "", numero: true },
  { titulo: "Se envía a", ancho: 36, valor: (f) => f.destinatarios },
  { titulo: "Correo nuevo", ancho: 10, valor: (f) => siNo(f.correoNuevo) },
  { titulo: "Motivo", ancho: 70, valor: (f) => f.motivo },
];

const celda = (columna, fila) => {
  const valor = columna.valor(fila);
  if (columna.estilo) return celdaTexto(valor ?? "", columna.estilo(fila));
  if (columna.numero) return celdaNumero(valor === "" ? "" : valor, numeroStyle);
  return celdaTexto(valor ?? "");
};

const hojaDeFilas = (filas) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([
    COLUMNAS.map((columna) => ({ v: columna.titulo, t: "s", s: headerStyle })),
    ...filas.map((fila) => COLUMNAS.map((columna) => celda(columna, fila))),
  ]);
  hoja["!cols"] = COLUMNAS.map((columna) => ({ wch: columna.ancho }));
  hoja["!rows"] = [{ hpt: 30 }];
  return hoja;
};

// Cuántas filas hay de cada desenlace, en el orden de ESTADOS (el mismo de la pantalla).
const hojaResumen = (filas) => {
  const conteo = {};
  filas.forEach((fila) => {
    conteo[fila.estadoPrevisto] = (conteo[fila.estadoPrevisto] || 0) + 1;
  });

  const estados = Object.keys(ESTADOS).filter((estado) => conteo[estado] > 0);
  // Por si llegara un estado que no está en ESTADOS: también se cuenta.
  Object.keys(conteo).forEach((estado) => {
    if (!estados.includes(estado)) estados.push(estado);
  });

  const hoja = XLSXStyle.utils.aoa_to_sheet([
    [
      { v: "Qué va a pasar", t: "s", s: headerStyle },
      { v: "Filas", t: "s", s: headerStyle },
      { v: "¿Se procesa?", t: "s", s: headerStyle },
    ],
    ...estados.map((estado) => [
      celdaTexto(etiquetaEstado(estado), estiloEstado(estado)),
      celdaNumero(conteo[estado]),
      celdaTexto(ESTADOS_A_REVISAR.includes(estado) ? "No, revisar a mano" : "Sí"),
    ]),
    [
      celdaTexto("Total", totalTextoStyle),
      celdaNumero(filas.length, { ...numeroStyle, font: { bold: true } }),
      celdaTexto("", totalTextoStyle),
    ],
  ]);
  hoja["!cols"] = [{ wch: 28 }, { wch: 10 }, { wch: 22 }];
  hoja["!rows"] = [{ hpt: 24 }];
  return hoja;
};

/**
 * @param {Array} filas  `datos` de la vista previa, en orden de archivo
 */
export const construirLibroVistaPrevia = (filas) => {
  const todas = [...(filas || [])].sort(
    (a, b) => (Number(a.numeroFila) || 0) - (Number(b.numeroFila) || 0),
  );
  const libro = XLSXStyle.utils.book_new();

  XLSXStyle.utils.book_append_sheet(libro, hojaDeFilas(todas), "Vista previa");

  // Siempre se agrega, aunque vaya vacía: en blanco dice "no hay nada que revisar".
  XLSXStyle.utils.book_append_sheet(
    libro,
    hojaDeFilas(todas.filter((fila) => ESTADOS_A_REVISAR.includes(fila.estadoPrevisto))),
    "Revisar",
  );

  XLSXStyle.utils.book_append_sheet(libro, hojaResumen(todas), "Resumen");

  return libro;
};

export const descargarVistaPrevia = (filas) => {
  XLSXStyle.writeFile(construirLibroVistaPrevia(filas), NOMBRE_ARCHIVO_VISTA_PREVIA);
};
